import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { AnimationOnScroll } from 'react-animation-on-scroll';
import { useForm } from 'react-hook-form';
import { Container, Feedback } from '../../components';
import { IFormContact } from '../../components/form-contact';
import { useModal } from '../../hooks';
import { sendMessage } from '../../services/contact.service';
import { subscribeSchema } from '../../validation/submit.schema';
import KetoButton from './keto-button';
import styles from './keto-contact.module.css';
import KetoPaper from './keto-paper';
import KetoTextField from './keto-text-field';
import KetoTextarea from './keto-textarea';
import KetoTypography, { KetoTypographyVariant } from './keto-typography';

export default function KetoContact() {
  const [mobile, setMobile] = useState<boolean>(false);
  const { setContent, open } = useModal();
  const resizeWindow = () => {
    if (window.matchMedia('(max-width: 767px)').matches) {
      setMobile(true);
    } else {
      setMobile(false);
    }
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener('resize', resizeWindow);
    return () => {
      window.removeEventListener('resize', resizeWindow);
    };
  }, []);

  const [disabled, setDisabled] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormContact>({
    mode: 'onBlur',
    resolver: yupResolver(subscribeSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      message: '',
    },
  });

  const onSubmit = async (data: IFormContact) => {
    setDisabled(true);
    const response = await sendMessage(data);

    if (String(response.status).startsWith('2')) {
      setDisabled(false);
      setContent(<Feedback />);
      open();
      reset();
    }
  };

  return (
    <section className={styles.contact}>
      <Container>
        <AnimationOnScroll
          offset={400}
          animateOnce
          duration={1.2}
          animateIn="animate__fadeInUp"
          className={styles.contact_inner}
        >
          <form
            className={cn(styles.contact_start, {
              [styles.disabled]: disabled === true,
            })}
          >
            <KetoTypography
              className={styles.contact_title}
              variant={KetoTypographyVariant.HEADLINE_1}
              tag="h2"
            >
              Let’s bring your idea to life.
            </KetoTypography>
            <KetoPaper>
              <KetoTypography
                className={styles.contact_text}
                variant={KetoTypographyVariant.BODY_1}
                tag="p"
              >
                Have a project in mind or just want to say hi? Drop us a message
                and our team will get back to you shortly.
              </KetoTypography>
              <div className={styles.contactForm_wrapper}>
                <KetoTextField
                  {...register('name')}
                  error={!!errors.name}
                  errorMessage={errors.name?.message}
                  placeholder="Your name"
                  className={styles.contact_name}
                />
                <KetoTextField
                  {...register('email')}
                  error={!!errors.email}
                  errorMessage={errors.email?.message}
                  placeholder="Enter your e-mail"
                  className={styles.contact_email}
                />
                <KetoTextField
                  {...register('phone')}
                  error={!!errors.phone}
                  errorMessage={errors.phone?.message}
                  variant="mask"
                  mask="+9999999999999"
                  maskPlaceholder={null}
                  placeholder="Phone Number"
                  className={styles.contact_number}
                />
                <KetoTextarea
                  {...register('message')}
                  error={!!errors.message}
                  errorMessage={errors.message?.message}
                  placeholder="Text message"
                  className={styles.contact_message}
                />
                <KetoButton
                  onClick={handleSubmit(onSubmit)}
                  fullWidth
                  variant="contained"
                >
                  Submit
                </KetoButton>
              </div>
            </KetoPaper>
          </form>
          {!mobile && (
            <div className={styles.contact_end}>
              <div className={styles.contact_wrapper}>
                <Image
                  alt="icons"
                  width={771}
                  height={565}
                  src="/keto/icons/Group.png"
                />
              </div>
              <Image
                alt="cook photo"
                width={740}
                height={853}
                src="/keto/contact/man.png"
              />
            </div>
          )}
        </AnimationOnScroll>
      </Container>
    </section>
  );
}
